import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {
  Col,
  Divider,
  Form,
  Layout,
  Modal,
  Row,
  Typography,
  message,
} from 'antd';

import { PageSpinner } from 'components/page-spinner';
import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import AboutServices from 'services/about';

import { errHandling } from 'utils';

import { valueData } from './mockData';
import './style.scss';

const { Content } = Layout;
const { Title, Text } = Typography;
const { confirm } = Modal;

const FIELDS = {
  ABOUT_PATH: '',
  DESCRIPTION: 'description',
  DESCRIPTION_BAHASA: 'description_idn',
  IMAGE: 'image_url',
  VISION: 'vision',
  VISION_BAHASA: 'vision_idn',
};

export function AboutUsCreateEdit() {
  const [form] = Form.useForm();
  const [changed, setChanged] = useState('');
  const [formLayout] = useState('vertical');
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageContent, setPageContent] = useState({});
  const [valueContent, setValueContent] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
  ];

  const createEditComponent = {
    brief_desc: (
      <div className="brief-desc-form">
        <Row className="pd-margin-top-lg">
          <Col lg={11}>
            <Form.Item label="Description" {...formItemLayout}>
              <ReactQuill
                modules={modules}
                formats={formats}
                value={get(pageContent, FIELDS.DESCRIPTION, '')}
                onChange={changeValue.bind(this, FIELDS.DESCRIPTION)}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 11, offset: 2 }}>
            <Form.Item label="Description (Bahasa)" {...formItemLayout}>
              <ReactQuill
                modules={modules}
                formats={formats}
                value={get(pageContent, FIELDS.DESCRIPTION_BAHASA, '')}
                onChange={changeValue.bind(this, FIELDS.DESCRIPTION_BAHASA)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Form.Item label="Image (1039 x 600)" {...formItemLayout}>
            <PdUploader
              alt="about-image"
              imgUrl={pageContent[FIELDS.IMAGE]}
              afterUpload={handleUploadAboutImage}
            />
          </Form.Item>
        </Row>
      </div>
    ),
    vision_mission: (
      <Row className="pd-margin-top-lg">
        <Col lg={11}>
          <Form.Item label="Description" {...formItemLayout}>
            <ReactQuill
              modules={modules}
              formats={formats}
              value={get(pageContent, FIELDS.VISION, '')}
              onChange={changeValue.bind(this, FIELDS.VISION)}
            />
          </Form.Item>
        </Col>
        <Col lg={{ span: 11, offset: 2 }}>
          <Form.Item label="Description (Bahasa)" {...formItemLayout}>
            <ReactQuill
              modules={modules}
              formats={formats}
              value={get(pageContent, FIELDS.VISION_BAHASA, '')}
              onChange={changeValue.bind(this, FIELDS.VISION_BAHASA)}
            />
          </Form.Item>
        </Col>
      </Row>
    ),
    value: valueContent.map(
      ({ description, description_idn, id, image_url, title } = {}, idx) => (
        <>
          <Row
            key={id}
            className="pd-margin-top-lg value-content"
            type="flex"
            align="middle">
            <Col lg={3} className="value-image-container">
              <img alt="value-img" src={image_url} />
            </Col>
            <Col lg={{ span: 13, offset: 1 }}>
              <Row>
                <Title level={4}>{title}</Title>
              </Row>
              <Row>
                <Text>{description}</Text>
              </Row>
              <Row className="pd-margin-top-sm">
                <Text>{description_idn}</Text>
              </Row>
            </Col>
            <Col lg={{ span: 6, offset: 1 }}>
              <Row>
                <Col lg={11}>
                  <PdButton
                    className="w-full height-32"
                    onClick={handleEditValue.bind(this, id)}
                    type="primary">
                    Edit
                  </PdButton>
                </Col>
                <Col lg={{ span: 11, offset: 2 }}>
                  <PdButton
                    className="w-full height-32"
                    onClick={handleDeleteValue.bind(this, id)}
                    type="danger">
                    Delete
                  </PdButton>
                </Col>
              </Row>
            </Col>
          </Row>
          {idx !== valueData.length - 1 && <Divider />}
        </>
      )
    ),
  };

  function handleAddValue() {
    navigate('/about_us_pages/value/create');
  }

  function handleEditValue(id) {
    navigate({
      pathname: '/about_us_pages/value/edit',
      search: `?id=${id}`,
    });
  }

  function handleDeleteValue(id) {
    confirm({
      title: 'Are you sure you want to delete it?',
      async onOk() {
        await AboutServices.deleteCompanyValue(id);
        getPageContentData();
        message.success('Successfully delete the value');
      },
    });
  }

  function handleUploadAboutImage(imgUrl) {
    setPageContent((prev) => ({ ...prev, [FIELDS.IMAGE]: imgUrl }));
  }

  async function getPageContentData() {
    setLoadingPage(true);
    try {
      switch (currentPath) {
        case '/about_us_pages/brief_desc':
          FIELDS.ABOUT_PATH = 'brief_desc';
          setChanged('Brief Description');
          break;
        case '/about_us_pages/vision_mission':
          FIELDS.ABOUT_PATH = 'vision_mission';
          setChanged('Vision & Mission');
          break;
        case '/about_us_pages/value':
          FIELDS.ABOUT_PATH = 'value';
          setChanged('Value');
          break;
        default:
          FIELDS.ABOUT_PATH = 'about_us_pages';
          setChanged('About');
          break;
      }
      // TODO: sync with BE API
      const isValue = FIELDS.ABOUT_PATH === 'value' || undefined;
      const {
        data: { data = {} },
      } = await AboutServices.getAbout(isValue);

      if (isValue) setValueContent(data);
      else setPageContent(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    getPageContentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(values) {
    try {
      setLoadingButton(true);
      const payload = { about_us: { ...pageContent, ...values } };
      await AboutServices.updateAbout(payload);
      message.success(`Successfully change ${changed}`);
      navigate({
        pathname: '/about_us_pages',
      });
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(key, value) {
    if (value) {
      setPageContent((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    }
  }

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Content className="pageContent">
      <Form
        form={form}
        layout={formLayout}
        onFinish={(values) => handleSubmit(values)}>
        <Layout className="pd-cms-about-us main-container">
          <div className="section-container">
            <div className="inner-section">
              <Row>
                <div className="pageTitle w-full">
                  <Col lg={12} style={{ fontSize: 38, fontWeight: 'bolder' }}>
                    {changed}
                  </Col>
                  {FIELDS.ABOUT_PATH === 'value' && (
                    <Col lg={{ span: 4, offset: 8 }}>
                      <PdButton
                        className="height-32 w-full"
                        onClick={handleAddValue}
                        type="primary">
                        Add New Value
                      </PdButton>
                    </Col>
                  )}
                </div>
              </Row>
              {createEditComponent[FIELDS.ABOUT_PATH]}
              {FIELDS.ABOUT_PATH !== 'value' && (
                <Form.Item className="pd-margin-top-lg">
                  <PdButton
                    className="height-32"
                    full
                    type="primary"
                    htmlType="submit"
                    loading={loadingButton}>
                    Publish
                  </PdButton>
                </Form.Item>
              )}
            </div>
          </div>
        </Layout>
      </Form>
    </Content>
  );
}

export default AboutUsCreateEdit;
